import { AgeCategory } from '../../../models/AgeCategory';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { PriceTier, serializePriceTier } from '../../../models/PriceTier';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { TFunction } from 'i18next';
import {
    AddBulkFromForeignLanguageProductsByFilterRequest,
    addBulkFromLanguageLearningByFilter
} from './addBulkFromLanguageLearningByFilter';
import { SubscriptionPeriodType } from '../../../models/Article';
import { addBulkFromLoteByFilter } from './addBulkFromLoteByFilter';
import { BulkAddProductsToSelectionListResponse, ProductFilter } from '../../store/api';
import { LanguageScope } from '../../shop/language/useLanguages';
import { mapLoanFormatStateToDto } from '../../../models/LoanFormatDto';
import { bulkUpdateToList } from './bulkUpdateToList';
import { addBulkFromCatalogueByIds, AddBulkFromCatalogueByIdsRequest } from './addBulkFromCatalogueByIds';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';
import { LOANFORMAT_ALL_OBJECT } from '../../shared/loanFormat/constants';

type GetRequestParams = {
    selectionListId: string;
    ageCategory: AgeCategory;
    productSelection: ProductSelection;
    filterRequest: ProductFilter;
    loanFormat: LoanFormatState;
    performChecks: boolean;
    language: string | undefined;
    source: LanguageScope.LANGUAGE_LEARNING | LanguageScope.LOTE;
    shopLanguage: string | undefined;
    priceTier: PriceTier;
    subscriptionPeriodType: SubscriptionPeriodType;
};

export const buildAddProductsCallback = (
    {
        selectionListId,
        ageCategory,
        productSelection,
        filterRequest,
        loanFormat,
        priceTier,
        performChecks,
        language,
        source,
        shopLanguage,
        subscriptionPeriodType
    }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        if (isExcludeProductSelection(productSelection)) {
            const body: AddBulkFromForeignLanguageProductsByFilterRequest = {
                filterRequest,
                excludedProductIds: productSelection.excluded.map(({ productId }) => productId),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType,
                loanFormat: mapLoanFormatStateToDto(loanFormat),
                ageCategory: ageCategory,
                language: language ?? null,
                shopLanguage
            };

            if (source === LanguageScope.LOTE) {
                return addBulkFromLoteByFilter({ selectionListId, body, params: { performChecks } });
            }

            return addBulkFromLanguageLearningByFilter({ selectionListId, body, params: { performChecks } });
        } else if (isIncludeProductSelection(productSelection)) {
            const body: AddBulkFromCatalogueByIdsRequest = {
                productIds: productSelection.included.map(({ productId }) => productId),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType,
                checkEpress: loanFormat.key === LOANFORMAT_ALL_OBJECT.key
            };
            return addBulkFromCatalogueByIds({
                selectionListId,
                body,
                params: { performChecks }
            });
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props = {
    ageCategory: AgeCategory;
    loanFormat: LoanFormatState;
    language: string | undefined;
    source: LanguageScope.LANGUAGE_LEARNING | LanguageScope.LOTE;
    shopLanguage: string | undefined;
    onSuccess: () => void;
    productFilter: ProductFilter;
};

export const useAddFromForeignLanguageToList = ({
    ageCategory,
    loanFormat,
    language,
    source,
    shopLanguage,
    onSuccess,
    productFilter
}: Props) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        addFromForeignLanguageToListBulk: (
            listId: string,
            priceTier: PriceTier,
            subscriptionPeriodType: SubscriptionPeriodType,
            performChecks: boolean,
            productSelection: ProductSelection
        ) => {
            const apiCallback = buildAddProductsCallback(
                {
                    selectionListId: listId,
                    ageCategory,
                    productSelection,
                    filterRequest: productFilter,
                    loanFormat,
                    priceTier,
                    performChecks,
                    language,
                    source,
                    shopLanguage,
                    subscriptionPeriodType
                },
                t
            );

            return bulkUpdateToList({
                setLoading,
                apiCallback,
                t,
                onSuccess
            });
        }
    };
};
